import { ApolloError, ObservableQuery } from '@apollo/client';
import { ReactNode, createContext, useContext, useEffect, useMemo, useRef } from 'react';

import {
  GetEventQuery as GetEvent,
  GetEventQueryVariables as GetEventVariables,
} from '__generated__/graphql';

import { EmbedContext } from './EmbedProvider';
import { Event, TicketCategory } from '../Checkout/helpers';
import { ProjectProvider } from '../../common/useProject';
import { useBrandColor } from '../../common/Common/ThemeProvider';
import GetEventQuery from './GetEventQuery';
import LocaleProvider from '../../common/LocaleProvider';
import StateContext from '../Checkout/StateContext';
import useQuery from '../../api/useQuery';

interface EventContextValue {
  event?: Event;
  ticketCategories?: TicketCategory[];
  loading?: boolean;
  error?: ApolloError;
  refetch?: ObservableQuery<GetEvent, GetEventVariables>['refetch'];
}

export const EventContext = createContext({} as EventContextValue);

export const useEvent = () => useContext(EventContext);

interface EventProviderProps {
  eventId?: string;
  children: ReactNode;
}

const EventProvider = ({ eventId, children }: EventProviderProps) => {
  const { form, cleanState, session } = useContext(StateContext);
  const { embedded, open } = useContext(EmbedContext);

  const { data, loading, refetch, error } = useQuery(
    GetEventQuery,
    {
      variables: {
        id: eventId,
        invitationCode: form.invitation_code,
        coupon: form.coupon,
      },
      skip: !eventId,
    },
  );

  const event = data?.event;
  const themeInitialized = useBrandColor(event?.brand_color, !!event);

  /**
   * Only show non-empty ticket categories. If the URL contains ticket category IDs,
   * hide the other ticket categories.
   */
  const ticketCategories = useMemo(() => event?.ticket_categories.filter(
    (ticketCategory) => ticketCategory.tickets_for_sale.length > 0
      && (session.ticketCategoryIds.length === 0 || session.ticketCategoryIds.includes(ticketCategory.id)),
  ) || [], [event, session.ticketCategoryIds]);

  const ticketCategoriesRef = useRef(ticketCategories);

  // Clean the state (e.g., remove tickets that no longer exist) when the event changes.
  useEffect(() => {
    if (event && ticketCategories !== ticketCategoriesRef.current) {
      cleanState(event);
      ticketCategoriesRef.current = ticketCategories;
    }
  }, [ticketCategories, event, cleanState]);

  const ready = themeInitialized && event && event.id === eventId;

  // Fetch the latest data when the embed is (re)opened
  useEffect(() => {
    if (embedded && open) {
      refetch();
    }
  }, [embedded, open, refetch]);

  const value = {
    event: ready ? event : undefined,
    ticketCategories: ready ? ticketCategories : undefined,
    loading,
    error,
    refetch,
  };

  return (
    <EventContext.Provider value={value}>
      <ProjectProvider project={event?.project}>
        <LocaleProvider
          localeFallback={event?.locale}
          localizeRequests
          currency={event?.project.currency}
          timezone={event?.project.timezone}
        >
          {children}
        </LocaleProvider>
      </ProjectProvider>
    </EventContext.Provider>
  );
};

export default EventProvider;
